import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Chip, IconButton, Button,
    LinearProgress, Select, MenuItem, TextField, CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useNavigate } from 'react-router-dom';

const LINE_API_URL = process.env.REACT_APP_LINE_API_URL || 'https://booking.trailernchu.co.jp/sys/line/api_v2.php';
const API_KEY = 'nc-line-sync-key-2024';
const PAGE_SIZE = 20;

interface AiActionLogEntry {
    id: number;
    action_type: 'suggest_reply' | 'auto_link_a' | 'auto_link_b' | 'auto_link_c' | 'recent_summary';
    line_user_id: string;
    line_display_name: string | null;
    custom_display_name: string | null;
    trigger_content: string | null;
    ai_reply: string | null;
    ai_action_type: string | null;
    ai_confidence: number | null;
    ai_reasoning: string | null;
    alert_level: number | null;
    scenario: 'A' | 'B' | 'C' | null;
    matched_reservation_id: string | null;
    match_score: number | null;
    candidate_count: number | null;
    candidates_json: any[] | null;
    created_at: string;
}

interface Summary {
    [key: string]: { count: number };
}

const ACTION_TYPE_COLORS: Record<string, string> = {
    suggest_reply: '#7c3aed',
    auto_link_a: '#10b981',
    auto_link_b: '#f59e0b',
    auto_link_c: '#94a3b8',
    recent_summary: '#0ea5e9',
};

const ACTION_TYPE_LABELS: Record<string, string> = {
    suggest_reply: 'AI候補生成',
    auto_link_a: '自動紐付け (A)',
    auto_link_b: '候補あり (B)',
    auto_link_c: '候補なし (C)',
    recent_summary: '直近概要',
};

const AI_ACTION_COLORS: Record<string, string> = {
    auto_reply: '#10b981',
    escalate: '#ef4444',
    hold: '#f59e0b',
};

const AI_ACTION_LABELS: Record<string, string> = {
    auto_reply: 'AUTO_REPLY',
    escalate: 'ESCALATE',
    hold: 'HOLD',
};

const ALERT_COLORS = ['transparent', '#2563eb', '#f59e0b', '#ef4444'];
const ALERT_LABELS = ['なし', 'Lv1 保留', 'Lv2 料金/時間', 'Lv3 緊急'];

function displayName(row: AiActionLogEntry): string {
    return row.custom_display_name || row.line_display_name || row.line_user_id;
}

function SuggestReplyCard({ row }: { row: AiActionLogEntry }) {
    const conf = Number(row.ai_confidence ?? 0);
    return (
        <>
            {/* トリガーメッセージ */}
            <Box sx={{ p: 1.5, mb: 1, background: '#f1f5f9', borderRadius: 2, borderLeft: '3px solid #3b82f6' }}>
                <Typography variant="caption" sx={{ color: '#3b82f6', fontWeight: 700 }}>顧客メッセージ</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{row.trigger_content}</Typography>
            </Box>

            {/* AI返信候補 */}
            <Box sx={{ p: 1.5, mb: 1, background: '#faf5ff', borderRadius: 2, borderLeft: `3px solid ${AI_ACTION_COLORS[row.ai_action_type ?? ''] || '#94a3b8'}` }}>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Typography variant="caption" sx={{ color: '#7c3aed', fontWeight: 700 }}>AI返信候補</Typography>
                    {row.ai_action_type && (
                        <Chip
                            label={AI_ACTION_LABELS[row.ai_action_type] || row.ai_action_type}
                            size="small"
                            sx={{ background: AI_ACTION_COLORS[row.ai_action_type] || '#e2e8f0', color: 'white', fontWeight: 800, fontSize: '10px', height: 20 }}
                        />
                    )}
                    {(row.alert_level ?? 0) > 0 && (
                        <Chip
                            label={ALERT_LABELS[row.alert_level!]}
                            size="small"
                            sx={{ background: ALERT_COLORS[row.alert_level!], color: 'white', fontWeight: 800, fontSize: '10px', height: 20 }}
                        />
                    )}
                    <Box display="flex" alignItems="center" gap={0.5} ml="auto">
                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>信頼度</Typography>
                        <Box sx={{ width: 60 }}>
                            <LinearProgress
                                variant="determinate"
                                value={conf * 100}
                                sx={{
                                    height: 6, borderRadius: 3, bgcolor: '#e2e8f0',
                                    '& .MuiLinearProgress-bar': {
                                        bgcolor: conf >= 0.8 ? '#10b981' : conf >= 0.5 ? '#f59e0b' : '#ef4444',
                                    },
                                }}
                            />
                        </Box>
                        <Typography variant="caption" fontWeight={700}>{(conf * 100).toFixed(0)}%</Typography>
                    </Box>
                </Box>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{row.ai_reply}</Typography>
            </Box>

            {/* 推論理由 */}
            {row.ai_reasoning && (
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                    📋 {row.ai_reasoning}
                </Typography>
            )}
        </>
    );
}

function AutoLinkCard({ row }: { row: AiActionLogEntry }) {
    const score = Number(row.match_score ?? 0);
    return (
        <Box sx={{ p: 1.5, background: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
            {row.scenario === 'A' && (
                <>
                    <Typography variant="body2" fontWeight={700} sx={{ color: '#10b981', mb: 0.5 }}>✅ 自動紐付け完了</Typography>
                    <Typography variant="body2">予約ID: <strong>{row.matched_reservation_id}</strong></Typography>
                    <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>マッチスコア</Typography>
                        <Box sx={{ width: 80 }}>
                            <LinearProgress
                                variant="determinate"
                                value={score * 100}
                                sx={{
                                    height: 6, borderRadius: 3, bgcolor: '#e2e8f0',
                                    '& .MuiLinearProgress-bar': { bgcolor: '#10b981' },
                                }}
                            />
                        </Box>
                        <Typography variant="caption" fontWeight={700}>{(score * 100).toFixed(0)}%</Typography>
                    </Box>
                </>
            )}
            {row.scenario === 'B' && (
                <>
                    <Typography variant="body2" fontWeight={700} sx={{ color: '#f59e0b', mb: 0.5 }}>🔍 候補あり・スタッフ確認待ち</Typography>
                    <Typography variant="body2">候補数: <strong>{row.candidate_count}件</strong></Typography>
                    {score > 0 && (
                        <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                            <Typography variant="caption" sx={{ color: '#64748b' }}>最高スコア</Typography>
                            <Box sx={{ width: 80 }}>
                                <LinearProgress
                                    variant="determinate"
                                    value={score * 100}
                                    sx={{
                                        height: 6, borderRadius: 3, bgcolor: '#e2e8f0',
                                        '& .MuiLinearProgress-bar': { bgcolor: '#f59e0b' },
                                    }}
                                />
                            </Box>
                            <Typography variant="caption" fontWeight={700}>{(score * 100).toFixed(0)}%</Typography>
                        </Box>
                    )}
                    {Array.isArray(row.candidates_json) && row.candidates_json.length > 0 && (
                        <Box mt={1}>
                            {row.candidates_json.map((c: any, i: number) => (
                                <Typography key={i} variant="caption" sx={{ display: 'block', color: '#374151' }}>
                                    • {c.customer_name}（{c.reservation_id}）スコア {(c.score * 100).toFixed(0)}%
                                </Typography>
                            ))}
                        </Box>
                    )}
                </>
            )}
            {row.scenario === 'C' && (
                <Typography variant="body2" fontWeight={700} sx={{ color: '#94a3b8' }}>📭 候補なし・ウェルカムメッセージ送信</Typography>
            )}
        </Box>
    );
}

function RecentSummaryCard({ row }: { row: AiActionLogEntry }) {
    return (
        <Box sx={{ p: 1.5, background: '#f0f9ff', borderRadius: 2, borderLeft: '3px solid #0ea5e9' }}>
            {row.trigger_content && (
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 1 }}>
                    {row.trigger_content}
                </Typography>
            )}
            <Box
                component="pre"
                sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'inherit',
                    fontSize: '13px',
                    lineHeight: 1.7,
                    margin: 0,
                    color: '#0f172a',
                }}
            >
                {row.ai_reply}
            </Box>
        </Box>
    );
}

export default function AiActionLog() {
    const navigate = useNavigate();
    const [data, setData] = useState<AiActionLogEntry[]>([]);
    const [total, setTotal] = useState(0);
    const [summary, setSummary] = useState<Summary>({});
    const [loading, setLoading] = useState(false);
    const [offset, setOffset] = useState(0);

    const [actionFilter, setActionFilter] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    const fetchData = useCallback(async (newOffset = 0) => {
        setLoading(true);
        const params = new URLSearchParams({
            action: 'get_ai_action_logs',
            api_key: API_KEY,
            limit: String(PAGE_SIZE),
            offset: String(newOffset),
        });
        if (actionFilter) params.set('action_type', actionFilter);
        if (dateFrom) params.set('date_from', dateFrom);
        if (dateTo) params.set('date_to', dateTo);

        try {
            const res = await fetch(`${LINE_API_URL}?${params}`);
            const json = await res.json();
            if (json.success) {
                setData(json.data);
                setTotal(json.total);
                setSummary(json.summary || {});
                setOffset(newOffset);
            }
        } catch (err) {
            console.error('Failed to fetch AI action logs:', err);
        }
        setLoading(false);
    }, [actionFilter, dateFrom, dateTo]);

    useEffect(() => { fetchData(0); }, [fetchData]);

    const totalCount = Object.values(summary).reduce((s, v) => s + v.count, 0);
    const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
    const totalPages = Math.ceil(total / PAGE_SIZE);

    return (
        <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 1, md: 3 } }}>
            {/* Header */}
            <Box display="flex" alignItems="center" gap={1} mb={2}>
                <IconButton onClick={() => navigate('/line')}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6" fontWeight={800}>AIアクションログ</Typography>
                <Typography variant="body2" sx={{ color: '#64748b', ml: 1 }}>{total}件</Typography>
            </Box>

            {/* Summary */}
            {totalCount > 0 && (
                <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    {Object.entries(summary).map(([type, s]) => (
                        <Box key={type} sx={{ textAlign: 'center' }}>
                            <Chip
                                label={ACTION_TYPE_LABELS[type] || type}
                                size="small"
                                sx={{ background: ACTION_TYPE_COLORS[type] || '#e2e8f0', color: 'white', fontWeight: 800, mb: 0.5 }}
                            />
                            <Typography variant="body2" fontWeight={700}>{s.count}件</Typography>
                        </Box>
                    ))}
                </Paper>
            )}

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Select
                    size="small"
                    value={actionFilter}
                    onChange={(e) => setActionFilter(e.target.value)}
                    displayEmpty
                    sx={{ minWidth: 160 }}
                >
                    <MenuItem value="">全アクション</MenuItem>
                    <MenuItem value="suggest_reply">AI候補生成</MenuItem>
                    <MenuItem value="auto_link_a">自動紐付け (A)</MenuItem>
                    <MenuItem value="auto_link_b">候補あり (B)</MenuItem>
                    <MenuItem value="auto_link_c">候補なし (C)</MenuItem>
                    <MenuItem value="recent_summary">直近概要</MenuItem>
                </Select>
                <TextField size="small" type="date" label="From" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
                <TextField size="small" type="date" label="To" value={dateTo} onChange={(e) => setDateTo(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
            </Paper>

            {/* Loading */}
            {loading && <LinearProgress sx={{ mb: 2 }} />}

            {/* Cards */}
            <Box display="flex" flexDirection="column" gap={2}>
                {data.map((row) => (
                    <Paper key={row.id} sx={{ p: 2, border: '1px solid #e2e8f0' }}>
                        {/* Card header */}
                        <Box display="flex" alignItems="center" gap={1} mb={1.5} flexWrap="wrap">
                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                                {new Date(row.created_at.replace(/-/g, '/')).toLocaleString('ja-JP')}
                            </Typography>
                            <Typography variant="body2" fontWeight={700}>
                                {displayName(row)}
                            </Typography>
                            <Chip
                                label={ACTION_TYPE_LABELS[row.action_type] || row.action_type}
                                size="small"
                                sx={{
                                    background: ACTION_TYPE_COLORS[row.action_type] || '#e2e8f0',
                                    color: 'white',
                                    fontWeight: 800,
                                    fontSize: '10px',
                                    height: 22,
                                }}
                            />
                        </Box>

                        {/* Card body */}
                        {row.action_type === 'suggest_reply' ? (
                            <SuggestReplyCard row={row} />
                        ) : row.action_type === 'recent_summary' ? (
                            <RecentSummaryCard row={row} />
                        ) : (
                            <AutoLinkCard row={row} />
                        )}
                    </Paper>
                ))}
            </Box>

            {!loading && data.length === 0 && (
                <Typography variant="body2" sx={{ color: '#64748b', textAlign: 'center', mt: 4 }}>
                    ログがありません
                </Typography>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <Box display="flex" justifyContent="center" alignItems="center" gap={2} mt={3}>
                    <IconButton onClick={() => fetchData(offset - PAGE_SIZE)} disabled={offset === 0 || loading}>
                        <NavigateBeforeIcon />
                    </IconButton>
                    <Typography variant="body2">{currentPage} / {totalPages}</Typography>
                    <IconButton onClick={() => fetchData(offset + PAGE_SIZE)} disabled={offset + PAGE_SIZE >= total || loading}>
                        <NavigateNextIcon />
                    </IconButton>
                </Box>
            )}
        </Box>
    );
}
